import * as React from "react";
import { useDispatch, useSelector } from 'react-redux';
import Slider from "react-slick";
import moment from "moment";
import { convertPascalCaseToWords } from "../../core/utils";
import { Project, PropertyDetails, Unit } from "../../core/api/models/project";
import api from "../../api";
import { toastr } from "react-redux-toastr";
import { AppPermissions, State, setShortListPropertyData, useHasPermission } from "../../core";
import BlankProPic from "../../styles/images/no-user-sm.png";
import PhoneNumberCopy from "../../components/phoneNumberCopy/PhoneNumberCopy";
import EmailLink from "../../components/emailLink/EmailLink";
import RequirementPropertyDetailPage from "../requirement/components/RequirementPropertyDetail";
import PropertyDetailPage from "../property-details/PropertyDetailPage";

const ShortListedProperty: React.FunctionComponent<{
  requirementId?: string;
  sortList?: any[];
  showCheckbox?: boolean;
  reload?: () => void;
}> = (props) => {
  const [propDropdown, setPropDropdown] = React.useState<number | null>(null);
  const settings = {
    className: "require-slider",
    dots: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
  };
  const [updating, setUpdating] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [dropPos, setDropPos] = React.useState<{
    open: boolean;
    x: string;
    y: string;
    id: string;
    fieldName: string;
  }>();
    const [projectDetailModal, setProjectDetailModal] = React.useState<{
      isOpen?: boolean;
      projectId?: string;
    }>({});
    const [propertyDetailModal, setPropertyDetailModal] = React.useState<{
      isOpen?: boolean;
      propertyId?: string;
    }>({});
    const canViewProject = useHasPermission(AppPermissions.ViewProject);
  const dispatch = useDispatch();
  const canViewProperty = useHasPermission(AppPermissions.ViewProperty);


  const onDeleteShortList = async (ids: string[]) => {
    setUpdating(true);
    try {
      await api.shortlist.deleteShortlistedProperties(ids);
      props.reload?.();
      setTimeout(() => {
        toastr.success("Success", "Delete the unit / Property");
        setDeleteModalOpen(false);
        setUpdating(false);
      }, 1000);
    } catch (error) {
      toastr.error("Error", "Failed to Approve Status");
      setUpdating(false);
    }
  };

  const [sortOrder, setSortOrder] = React.useState("asc");

  const [groupByList, setGroupByList] = React.useState<any>();

  const [selectedProperty, setSelectedProperty] = React.useState<any[]>();

  const onAllSelected = (selected: boolean, projectId: string) => {
    const allValues = !selected
      ? ([] as any[])
      : props.sortList?.filter(
        (s) => s.propertyDetailsFromProject?.projectId === projectId
      );
    setSelectedProperty([
      ...(selectedProperty?.filter(
        (p) => p.propertyDetailsFromProject?.projectId !== projectId
      ) || []),
      ...(allValues || []),
    ]);
    const tempArr = [
      ...(selectedProperty?.filter(
        (p) => p.propertyDetailsFromProject?.projectId !== projectId
      ) || []),
      ...(allValues || []),]

    dispatch(setShortListPropertyData(tempArr?.map((x) => {
      return {
        ...x.propertyDetailsFromProject,
        latitude: x?.propertyDetails?.proLatitude,
        longitude: x?.propertyDetails?.proLongitude,
        cityName: x?.propertyDetails?.cityName,
        cityLocName: x?.propertyDetails?.cityLocName,
      };
    }) || []))
  };

  const onSelectAllValues = (selected: boolean) => {
    if (selected) {
      setSelectedProperty(props?.sortList || []);
      dispatch(setShortListPropertyData(props?.sortList?.map((x) => {
        return {
          ...x.propertyDetailsFromProject,
          latitude: x?.propertyDetails?.proLatitude,
          longitude: x?.propertyDetails?.proLongitude,
          cityName: x?.propertyDetails?.cityName,
          cityLocName: x?.propertyDetails?.cityLocName,
        };
      }) || []))
    } else {
      dispatch(setShortListPropertyData([]))
      setSelectedProperty([]);
    }
  };

  const onSelect = (id: string, sel: boolean) => {
    if (sel) {
      setSelectedProperty([
        ...(selectedProperty || []),
        props.sortList?.find((s) => s._id === id),
      ]);
      const tempArr = [
        ...(selectedProperty || []),
        props.sortList?.find((s) => s._id === id),
      ]
      dispatch(setShortListPropertyData(tempArr?.map((x) => {
        return {
          ...x.propertyDetailsFromProject,
          latitude: x?.propertyDetails?.proLatitude,
          longitude: x?.propertyDetails?.proLongitude,
          cityName: x?.propertyDetails?.cityName,
          cityLocName: x?.propertyDetails?.cityLocName,
        };
      }) || []))
    } else {
      setSelectedProperty(
        selectedProperty?.filter((val) => val._id !== id) || []
      );
      const tempArr = selectedProperty?.filter((val) => val._id !== id) || []
      dispatch(setShortListPropertyData(tempArr?.map(item => item?.propertyDetailsFromProject) || []))
    }
  };

  React.useEffect(() => {
    if (!!props.sortList?.length) {
      const groupList = props.sortList
        ?.sort((a, b) =>
          a.propertyDetailsFromProject?.projectName?.localeCompare(
            b.propertyDetailsFromProject?.projectName
          )
        )
        ?.reduce((group: any, product: any) => {
          const { propertyDetailsFromProject } = product;
          const { projectId } = propertyDetailsFromProject;

          group[projectId] = group[projectId] ?? [];
          group[projectId].push(product);
          return group;
        }, {});
      setGroupByList(groupList);
    }
  }, [props.sortList]);

  const onSort = (sort: string) => {
    setSortOrder(sort);
    const sortList =
      sort === "asc"
        ? props.sortList?.sort((a, b) =>
          a.propertyDetailsFromProject?.projectName?.localeCompare(
            b.propertyDetailsFromProject?.projectName
          )
        )
        : props.sortList?.sort((a, b) =>
          b.propertyDetailsFromProject?.projectName?.localeCompare(
            a.propertyDetailsFromProject?.projectName
          )
        );
    if (!!sortList?.length) {
      const groupList = sortList?.reduce((group: any, product: any) => {
        const { propertyDetailsFromProject } = product;
        const { projectId } = propertyDetailsFromProject;

        group[projectId] = group[projectId] ?? [];
        group[projectId].push(product);
        return group;
      }, {});
      setGroupByList(groupList);
    }
  };

  const ordinalSuffixOf = (i: number) => {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) {
      return i + "st";
    }
    if (j === 2 && k !== 12) {
      return i + "nd";
    }
    if (j === 3 && k !== 13) {
      return i + "rd";
    }
    return i + "th";
  };

  const renderDeleteConfirm = () => {
    return deleteModalOpen ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>Are you sure to delete the unit / Property</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-md-5 px-4 mr-3"
                        onClick={() => {
                          setDeleteModalOpen(false);
                        }}
                        disabled={updating}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 flex-fill"
                        disabled={updating}
                        onClick={() => {
                          onDeleteShortList?.(
                            selectedProperty?.map((x) => x._id) || []
                          );
                        }}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };


  const dropdownClick = (event: any, id: string, fieldName: string) => {
    const pos = event.target.getBoundingClientRect();
    const x = pos.x + "px";
    const y = pos.y + pos.height + "px";
    setDropPos({ open: !dropPos?.open, x, y, id, fieldName });
  };

  const renderFieldDetails = (fieldName: string, fieldArrayValues: any) => {
    return (
      <div
        className="position-absolute bg-white shadow w-100 pt-2"
        style={{ right: 0, maxWidth: "435px", zIndex: 100 }}
      >
        <div className="px-3 pt-2 font-normal text-gray-600 font-weight-semibold my-1">
          {fieldName}
        </div>
        {fieldArrayValues?.map((item: any, index: number) => {
          return (
            <div className="d-flex align-items-center text-nowrap py-2 px-3 border-bottom" key={index}>
              <img
                src={item?.profileImage ? item?.profileImage : BlankProPic}
                alt="image_not_found"
                className="img-cover rounded-circle img-responsive mr-2"
                width={26}
                height={26}
              />
              <div className="font-weight-semibold">
                <div className="font-normal">
                  {item.contactName}
                </div>
                <div className="font-xs text-gray-600 d-flex align-items-center">
                {item.phones.map((itm:any, idx:number)=><><PhoneNumberCopy>{itm.phone}</PhoneNumberCopy>{++idx < item?.phones?.length ? ', ' : '' }</>)}
                </div>
                <div className="font-xs text-gray-600 d-flex align-items-center">
                  {item.emails.map((itm:any, idx:number)=><><EmailLink>{itm.email}</EmailLink>{++idx < item.emails.length ? ', ' : ''}</>)}
                </div>
                <div className="font-xs text-gray-600 d-flex align-items-center">
                  {item?.type}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );

  };

  return (groupByList ? Object.keys(groupByList)?.length : 0) ? (
    <>
      <div className="pb-3 px-2 border-bottom d-flex align-items-center mb-1">
        <div className="font-sm text-gray-500">
          <span className="text-gray-700">{props.sortList?.length}</span>{" "}
          Properties,{" "}
          <span className="text-gray-700">
            {groupByList ? Object.keys(groupByList)?.length : 0}
          </span>{" "}
          Projects
        </div>
        <div
          className="inline-flex font-xs text-gray-500 border-left ml-4 pl-4 cursor-pointer"
          onClick={() => onSort(sortOrder === "asc" ? "dece" : "asc")}
        >
          {sortOrder === "asc" ? (
            <svg
              width="19"
              height="15"
              viewBox="0 0 19 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Group 2040">
                <g id="Group">
                  <path
                    id="Vector"
                    d="M8.65622 12.3984H0.579535C0.259458 12.3984 0 12.6579 0 12.978V13.5575C0 13.8776 0.259458 14.137 0.579535 14.137H8.65622C8.97624 14.137 9.23575 13.8776 9.23575 13.5575V12.978C9.23575 12.6579 8.9763 12.3984 8.65622 12.3984Z"
                    fill="#6F7171"
                  />
                  <path
                    id="Vector_2"
                    d="M0.579535 10.4847H7.56791C7.88793 10.4847 8.14745 10.2252 8.14745 9.90516V9.32563C8.14745 9.00555 7.88793 8.74609 7.56791 8.74609H0.579535C0.259458 8.74609 0 9.00555 0 9.32563V9.90516C0 10.2252 0.259458 10.4847 0.579535 10.4847Z"
                    fill="#6F7171"
                  />
                  <path
                    id="Vector_3"
                    d="M0.579535 6.83235H6.47949C6.79951 6.83235 7.05902 6.5729 7.05902 6.25282V5.67328C7.05902 5.35321 6.79951 5.09375 6.47949 5.09375H0.579535C0.259458 5.09375 0 5.35321 0 5.67328V6.25282C0 6.5729 0.259458 6.83235 0.579535 6.83235Z"
                    fill="#6F7171"
                  />
                  <path
                    id="Vector_4"
                    d="M0.579535 2.77767H5.39112C5.71114 2.77767 5.97066 2.51821 5.97066 2.19813V1.6186C5.97066 1.29852 5.71114 1.03906 5.39112 1.03906H0.579535C0.259458 1.03906 0 1.29852 0 1.6186V2.19813C0 2.51821 0.259458 2.77767 0.579535 2.77767Z"
                    fill="#6F7171"
                  />
                  <path
                    id="Vector_5"
                    d="M18.5371 9.73898L18.1273 9.32919C18.0187 9.22053 17.8712 9.15945 17.7175 9.15945C17.5638 9.15945 17.4165 9.22053 17.3077 9.32919L15.3145 11.3224V1.16547C15.3145 0.845395 15.055 0.585938 14.735 0.585938H14.1554C13.8354 0.585938 13.5759 0.845395 13.5759 1.16547V11.3223L11.5826 9.32914C11.4739 9.22041 11.3265 9.15939 11.1728 9.15939C11.0192 9.15939 10.8717 9.22047 10.7631 9.32914L10.3533 9.73892C10.127 9.96523 10.127 10.3322 10.3533 10.5585L14.0354 14.2406C14.1441 14.3493 14.2915 14.4104 14.4452 14.4104C14.5989 14.4104 14.7463 14.3493 14.855 14.2406L18.5371 10.5585C18.7634 10.3322 18.7634 9.96529 18.5371 9.73898Z"
                    fill="#6F7171"
                  />
                </g>
              </g>
            </svg>
          ) : (
            <svg
              width="20px"
              height="15px"
              viewBox="0 0 20 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g>
                <path
                  fill="#6F7171"
                  d="M10.031,2.197V1.618c0-0.319-0.26-0.579-0.58-0.579H1.375c-0.32,0-0.58,0.26-0.58,0.579v0.579
		c0,0.32,0.259,0.58,0.58,0.58h8.077C9.771,2.777,10.031,2.518,10.031,2.197z"
                />
                <path
                  fill="#6F7171"
                  d="M0.795,5.269v0.579c0,0.32,0.259,0.58,0.58,0.58h6.988c0.32,0,0.58-0.26,0.58-0.58V5.269
		c0-0.319-0.26-0.579-0.58-0.579H1.375C1.054,4.689,0.795,4.949,0.795,5.269z"
                />
                <path
                  fill="#6F7171"
                  d="M0.795,8.921V9.5c0,0.32,0.259,0.58,0.58,0.58h5.9c0.32,0,0.58-0.26,0.58-0.58V8.921
		c0-0.319-0.26-0.579-0.58-0.579h-5.9C1.054,8.342,0.795,8.602,0.795,8.921z"
                />
                <path
                  fill="#6F7171"
                  d="M0.795,12.978v0.579c0,0.32,0.259,0.58,0.58,0.58h4.812c0.32,0,0.58-0.26,0.58-0.58v-0.579
		c0-0.319-0.26-0.579-0.58-0.579H1.375C1.054,12.398,0.795,12.658,0.795,12.978z"
                />
              </g>
              <path
                fill="#6F7171"
                d="M19.332,4.439L15.65,0.758c-0.109-0.109-0.256-0.17-0.41-0.17s-0.301,0.061-0.41,0.17l-3.682,3.682
	c-0.227,0.227-0.227,0.594,0,0.82l0.41,0.409c0.107,0.108,0.256,0.17,0.409,0.17s0.302-0.062,0.409-0.17l1.994-1.993v10.156
	c0,0.32,0.26,0.58,0.579,0.58h0.579c0.32,0,0.58-0.26,0.58-0.58V3.676l1.993,1.993c0.108,0.108,0.257,0.17,0.41,0.17
	s0.301-0.062,0.409-0.17l0.41-0.409C19.559,5.033,19.559,4.666,19.332,4.439z"
              />
            </svg>
          )}
          <span className="ml-1">Sort</span>
        </div>
        <div className="inline-flex font-xs text-gray-500 border-left ml-4 pl-4 cursor-pointer btn-dt">
          <button
            className="btn font-sm font-weight-semibold border rounded p-0  mx-1"
            disabled={!selectedProperty?.length}
            style={{ width: "40px", height: "40px" }}
            title={"Delete shortlisted properties"}
            onClick={() => setDeleteModalOpen(true)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 4H3.33333H14"
                stroke="#B8B8B8"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M12.6693 4.00065V13.334C12.6693 13.6876 12.5288 14.0267 12.2787 14.2768C12.0287 14.5268 11.6896 14.6673 11.3359 14.6673H4.66927C4.31565 14.6673 3.97651 14.5268 3.72646 14.2768C3.47641 14.0267 3.33594 13.6876 3.33594 13.334V4.00065M5.33594 4.00065V2.66732C5.33594 2.3137 5.47641 1.97456 5.72646 1.72451C5.97651 1.47446 6.31565 1.33398 6.66927 1.33398H9.33594C9.68956 1.33398 10.0287 1.47446 10.2787 1.72451C10.5288 1.97456 10.6693 2.3137 10.6693 2.66732V4.00065"
                stroke="#B8B8B8"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M6.66406 7.33398V11.334"
                stroke="#B8B8B8"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M9.33594 7.33398V11.334"
                stroke="#B8B8B8"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          </button>
        </div>
      </div>
      {groupByList && (
        <div className="table-responsive shortListProperty-Table dd">
          <table className="table w-100 proposal-table-outer">
            <tr className="font-sm text-gray-600">
              {props.showCheckbox && (
                <th
                  className="py-3 border-bottom"
                  style={{ maxWidth: "20px", width: "20px" }}
                >
                  <div className="py-1">
                    <input
                      type="checkbox"
                      name={`checkprop-create-proposal`}
                      id={`checkprop-create-proposal`}
                      onChange={(e) => onSelectAllValues(e.target.checked)}
                      checked={
                        selectedProperty?.length ===
                        props.sortList?.length
                      }
                    />
                    <label htmlFor={`checkprop-create-proposal`}></label>
                  </div>
                </th>
              )}
              <th
                className="py-3 border-bottom"
                style={{ maxWidth: "20px", width: "20px" }}
              ></th>
              <th className="py-3 border-bottom font-weight-semibold ">
                Property
              </th>
              <th
                className="py-3 border-bottom font-weight-semibold  text-center"
                style={{ width: "194px" }}
              >
                Version
              </th>
              <th className="py-3 border-bottom font-weight-semibold ">
                Square feet area
              </th>
              <th className="py-3 border-bottom font-weight-semibold ">
                Developer
              </th>
              <th className="py-3 border-bottom font-weight-semibold ">
                Added Units
              </th>
              <th className="py-3 border-bottom font-weight-semibold"></th>
            </tr>
            {/* {[...Array(3)].map((item, index) => { */}
            {Object.keys(groupByList).map((key, index) => {
              const item = groupByList[key]?.find(
                (x: any) => x.propertyDetailsFromProject.projectId === key
              );

              const totalArea =
                groupByList[key]?.reduce(
                  (t: any, c: any) =>
                    t + Number(c.propertyDetailsFromProject?.area || 0),
                  0
                ) || 0;

              const unitDetails = groupByList[key]?.sort((a: any, b: any) =>
                a.propertyDetailsFromProject?.floor <
                  b.propertyDetailsFromProject?.floor
                  ? -1
                  : 1
              );

              const floors = [
                ...new Set(
                  unitDetails?.map(
                    (f: any) => f.propertyDetailsFromProject?.floor
                  )
                ),
              ].sort();

              return (
                <>
                  <tr
                    className={`require-row dd ${propDropdown === index ? "active" : ""
                      }`}
                  >
                    {props.showCheckbox && (
                      <td
                        className="py-2 border-bottom font-sm"
                        style={{ maxWidth: "40px", width: "40px" }}
                      >
                        <div className="py-1">
                          <input
                            type="checkbox"
                            name={`properyCheck-${item.propertyDetailsFromProject?.projectId}`}
                            id={`properyCheck-${item.propertyDetailsFromProject?.projectId}`}
                            onChange={(e) =>
                              onAllSelected(
                                e.target.checked,
                                item?.propertyDetailsFromProject?.projectId
                              )
                            }
                            checked={
                              selectedProperty?.filter(
                                (ff) =>
                                  ff?.propertyDetailsFromProject?.projectId ===
                                  item?.propertyDetailsFromProject?.projectId
                              )?.length === unitDetails?.length
                            }
                          />
                          <label
                            htmlFor={`properyCheck-${item.propertyDetailsFromProject?.projectId}`}
                          ></label>
                        </div>
                      </td>
                    )}

                    <td className="py-2 border-bottom"
                       role="button"
                       onClick={() =>
                           setProjectDetailModal({
                           isOpen: true,
                           projectId: item?.propertyDetailsFromProject?.projectId,
                         })
                       }
                    >
                      <div className="py-1 text-center">
                        <img
                          src={
                            item?.propertyDetailsFromProject
                              ?.propertyCoverImage ||
                            require(`../../assets/images/color-noimage-lg.png`)
                          }
                          alt={item?.propertyDetailsFromProject?.projectName}
                          width={59}
                          height={45}
                          className="img-cover"
                        />
                      </div>
                    </td>
                    <td
                      className="py-2 border-bottom"
                      style={{ width: "290px", maxWidth: "290px" }}
                      role="button"
                      onClick={() =>
                          setProjectDetailModal({
                          isOpen: true,
                          projectId: item?.propertyDetailsFromProject?.projectId,
                        })
                      }
                    >
                      <div className="py-1 w-100">
                        <div className="font-sm text-truncate mb-1">
                          {item?.propertyDetailsFromProject?.projectName}
                        </div>
                        <div className="font-xs text-gray-500 line-clamp-2">
                          {item?.propertyDetailsFromProject?.propertyAddress}
                        </div>
                      </div>
                    </td>
                    <td
                      className="py-2 border-bottom"
                      style={{ width: "194px" }}
                    >
                      <div className="py-1 font-sm text-center">
                        V{item?.requirementVersion}
                      </div>
                    </td>
                    <td className="py-2 border-bottom">
                      <div className="py-1 font-xs text-gray-800">
                        <b>{totalArea?.toFixed(2)}</b> /Sq. Ft
                      </div>
                    </td>
                    <td className="py-2 border-bottom">
                      <div className="py-1 font-xs text-gray-800 font-weight-semibold">
                        {item?.propertyDetailsFromProject?.developer}
                      </div>
                    </td>
                    {/* <td className="py-2 border-bottom" style={{ width: "100px" }}>
                    <div className="py-1 font-xs text-gray-800">
                      <b>₹ 50</b> /Sq. Ft
                    </div>
                  </td> */}
                    <td className="py-2 border-bottom">
                      <div className="py-1">
                        <div className="font-xs text-gray-600 border rounded-pill d-inline-flex py-1 px-2 align-items-center mr-2">
                          Floor:{" "}
                          {
                            groupByList[key][0]?.propertyDetailsFromProject
                              ?.floor
                          }{" "}
                          / Unit:{" "}
                          {
                            groupByList[key][0]?.propertyDetailsFromProject
                              ?.unitName
                          }{" "}
                          / Area:{" "}
                          {
                            groupByList[key][0]?.propertyDetailsFromProject
                              ?.area
                          }
                        </div>
                        {groupByList[key]?.length > 1 && (
                          <div className="font-xs text-gray-600 border rounded-pill d-inline-flex py-1 px-2 align-items-center mr-2">
                            +{groupByList[key]?.length - 1}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 border-bottom">
                      <div
                        className="py-1 cursor-pointer"
                        onClick={() =>
                          propDropdown === index
                            ? setPropDropdown(null)
                            : setPropDropdown(index)
                        }
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g id="chevron-down (98) 1">
                            <path
                              id="Vector"
                              d="M6 9L12 15L18 9"
                              stroke="#272B2A"
                              stroke-width="1.4"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </g>
                        </svg>
                      </div>
                    </td>
                  </tr>
                  {propDropdown === index && (
                    <tr>
                      <td colSpan={props.showCheckbox ? 8 : 7}>
                        <div
                          className="py-4 pr-4 pl-5"
                          style={{ maxWidth: "90vw" }}
                        >
                          <Slider {...settings}>
                            <div className="border rounded p-2 d-inline-block mr-1 w-auto">
                              <div className="font-sm font-weight-semibold title">
                                Matching Unit
                              </div>
                              <div className="font-xs font-weight-medium text-gray-700">
                                {
                                  unitDetails?.filter(
                                    (p: any) =>
                                      p.propertyDetailsFromProject
                                        .currentStatus === "available"
                                  )?.length
                                }{" "}
                                Available, {unitDetails?.length} Total
                              </div>
                            </div>
                            {floors?.map((fl) => {
                              const availableCount = unitDetails?.filter(
                                (x: any) =>
                                  x.propertyDetailsFromProject.currentStatus ===
                                  "available" &&
                                  x.propertyDetailsFromProject.floor === fl
                              )?.length;
                              const totalCount = unitDetails?.filter(
                                (x: any) =>
                                  x.propertyDetailsFromProject.floor === fl
                              )?.length;

                              return (
                                <>
                                  <div className="border rounded p-2 d-inline-block mr-1 w-auto">
                                    {fl === "0" ? (
                                      <div className="font-sm font-weight-semibold title">
                                        Ground Floor
                                      </div>
                                    ) : (
                                      <div className="font-sm font-weight-semibold title">
                                        {ordinalSuffixOf(Number(fl))} Floor
                                      </div>
                                    )}
                                    <div className="font-xs font-weight-medium text-gray-700">
                                      {availableCount} Available, {totalCount}{" "}
                                      Total
                                    </div>
                                  </div>
                                </>
                              );
                            })}
                          </Slider>
                        </div>
                        <div>
                          <table className="table table-bordered w-100 font-sm">
                            <tr>
                              {props.showCheckbox && (
                                <td
                                  className="py-2  px-2   border-bottom px-1"
                                  style={{ maxWidth: "40px", width: "40px" }}
                                >
                                  <div className="">
                                    <input
                                      type="checkbox"
                                      name={`propertyCheck-unit-${item.propertyDetailsFromProject?.projectId}`}
                                      id={`propertyCheck-px-2-${item.propertyDetailsFromProject?.projectId}`}
                                      onChange={(e) =>
                                        onAllSelected(
                                          e.target.checked,
                                          item?.propertyDetailsFromProject
                                            ?.projectId
                                        )
                                      }
                                      // checked={
                                      //   selectedProperty?.filter(
                                      //     (ff) => ff._id === item._id
                                      //   )?.length === unitDetails?.length
                                      // }
                                      checked={
                                        selectedProperty?.filter(
                                          (ff) =>
                                            ff?.propertyDetailsFromProject?.projectId ===
                                            item?.propertyDetailsFromProject?.projectId
                                        )?.length === unitDetails?.length
                                      }
                                    />
                                    <label
                                      htmlFor={`propertyCheck-px-2-${item.propertyDetailsFromProject?.projectId}`}
                                    ></label>
                                  </div>
                                </td>
                              )}

                              <td className="py-2 border-bottom">
                                <div className="py-1">Floor</div>
                              </td>
                              <td className="py-2 border-bottom">
                                <div className="py-1">Unit Id</div>
                              </td>
                              <td className="py-2 border-bottom">
                                <div className="py-1">Area (in Sq.Ft)</div>
                              </td>
                              <td className="py-2 border-bottom">
                                <div className="py-1">Interior Type</div>
                              </td>
                              <td className="py-2 border-bottom">
                                <div className="py-1">Quoted Rent</div>
                              </td>
                              <td className="py-2 border-bottom">
                                <div className="py-1">Current Status</div>
                              </td>
                              <td className="py-2 border-bottom">
                                <div className="py-1">Available From</div>
                              </td>
                              <td className="py-2 border-bottom font-sm">
                                <div className="py-1">Contact Details</div>
                              </td>
                            </tr>
                            {unitDetails?.map((unit: any) => {
                              const property = unit?.propertyDetailsFromProject;
                              const propertyDetails = unit?.propertyDetails;

                              return (
                                <>
                                  <tr
                                    role="button"
                                    onClick={() => {
                                      if (property?.currentStatus === "available") {
                                        setPropertyDetailModal({
                                          isOpen: true,
                                          propertyId: property.propertyId,
                                        })
                                      }
                                    }}
                                  >
                                    {props.showCheckbox && (
                                      <td
                                        className="py-3 px-2 border-bottom px-0"
                                        style={{
                                          maxWidth: "40px",
                                          width: "40px",
                                        }}
                                        onClick={(e) => e.stopPropagation()} 
                                      >
                                        <div className="">
                                          <input
                                            type="checkbox"
                                            name={`propertyCheck-${unit._id}-${property.projectId}`}
                                            id={`propertyCheck-${unit._id}-${property.projectId}`}
                                            onChange={(e) =>
                                              onSelect(
                                                unit._id,
                                                e.target.checked
                                              )
                                            }
                                            checked={
                                              !!selectedProperty?.find(
                                                (fv) =>
                                                  fv._id?.toString() ===
                                                  unit._id?.toString()
                                              )
                                            }
                                          />
                                          <label
                                            htmlFor={`propertyCheck-${unit._id}-${property.projectId}`}
                                          ></label>
                                        </div>
                                      </td>
                                    )}
                                    <td className="py-3 border-bottom">
                                      <div className="">{property.floor}</div>
                                    </td>
                                    <td className="py-3 border-bottom">
                                      <div className="">
                                        {property.unitName}
                                      </div>
                                    </td>
                                    <td className="py-3 border-bottom">
                                      <div className="">
                                        {property.area?.toFixed(2)}
                                      </div>
                                    </td>
                                    <td className="py-3 border-bottom">
                                      <div className="">
                                        {property?.interiorType}
                                      </div>
                                    </td>
                                    <td className="py-3 border-bottom">
                                      <div className="">
                                        {property?.quotedRent}{" "}
                                      </div>
                                    </td>
                                    <td className="py-3 border-bottom">
                                      <div className="">
                                        {convertPascalCaseToWords(
                                          property?.currentStatus
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-3 border-bottom">
                                      {property?.availableFrom &&
                                        property?.availableFrom !==
                                        "1970-01-01T00:00:00.000Z" && (
                                          <div className="">
                                            {moment(
                                              property?.availableFrom
                                            ).format("DD-MM-YYYY")}
                                          </div>
                                        )}
                                    </td>
                                    <td
                                       className="py-2 border-bottom"
                                        style={{ width: "290px", maxWidth: "290px" }}
                                        onClick={(e) => e.stopPropagation()}
                                        >
                                            <div role="button" className="py-1 w-100 font-xs font-weight-semibold position-relative"
                                             onClick={(e) => dropdownClick(e, propertyDetails?._id, 'ContactDetails')}
                                             >
                                                { propertyDetails?.contactDetails?.length
                                                        ? propertyDetails?.contactDetails?.length > 2
                                                            ? propertyDetails.contactDetails
                                                                .slice(0, 2)
                                                                .map((participant: any) =>
                                                                    participant
                                                                        ? `${participant.contactName || ''}`
                                                                        : ''
                                                                )
                                                                .join(", ")
                                                            : propertyDetails?.contactDetails?.map((participant: any) =>
                                                                participant
                                                                    ? `${participant.contactName || ''}`
                                                                    : ''
                                                            )
                                                                .join(", ")
                                                        : ""}
                                                <span
                                                    className="text-primary cursor-pointer"
                                                   
                                                >
                                                    {propertyDetails?.contactDetails?.length > 2 ? ` +${propertyDetails?.contactDetails?.length - 2}` : ''}
                                                    {propertyDetails._id === dropPos?.id && dropPos?.fieldName === 'ContactDetails' &&
                                                         dropPos?.open && (
                                                            renderFieldDetails('Contact Details', propertyDetails?.contactDetails)
                                                        )}
                                                </span>
                                            </div>
                                        </td>
                                  </tr>
                                </>
                              );
                            })}
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </table>
          {renderDeleteConfirm()}
        </div>
      )}
      
      {projectDetailModal.isOpen && canViewProject &&  (
        <RequirementPropertyDetailPage
          onClose={() => setProjectDetailModal({})}
          projectId={projectDetailModal.projectId}
          showDetailsOnly={true}
        ></RequirementPropertyDetailPage>
      )}
      {propertyDetailModal.isOpen && canViewProperty && (
        <PropertyDetailPage
          onClose={() => setPropertyDetailModal({})}
          projectId={propertyDetailModal.propertyId}
        ></PropertyDetailPage>
      )}
    </>
  ) : (
    <div className="py-4 d-flex flex-column align-items-center">
      <i className="icon-box--lg font-xxl bg-orange text-white icon-bulb rounded-circle d-inline-flex justify-content-center align-items-center"></i>
      <div
        className="py-2 font-xs font-weight-semibold text-center my-1"
        style={{ maxWidth: "500px", lineHeight: "21px" }}
      >
        No properties in Short Listed Property.
      </div>
    </div>
  );
};

export default ShortListedProperty;
